import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Collapse,
  Box,
} from "@mui/material";
import {
  Check as CheckIcon,
  Edit as EditIcon,
  Lock as LockIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";

const CompletionIcon = () => (
  <Box
    sx={{
      position: "relative",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      mb: 1.5,
      mt: 1,
      width: 88,
      height: 88,
    }}
  >
    <Box
      sx={{
        position: "absolute",
        top: 10,
        left: 4,
        width: 7,
        height: 7,
        bgcolor: "#90caf9",
        transform: "rotate(45deg)",
        borderRadius: "1px",
      }}
    />
    <Box
      sx={{
        position: "absolute",
        top: 22,
        right: 2,
        width: 6,
        height: 6,
        bgcolor: "#64b5f6",
        borderRadius: "50%",
      }}
    />
    <Box
      sx={{
        position: "absolute",
        bottom: 14,
        left: 0,
        width: 10,
        height: 10,
        bgcolor: "#bbdefb",
        transform: "rotate(45deg)",
        borderRadius: "1px",
      }}
    />
    <Box
      sx={{
        position: "absolute",
        bottom: 18,
        right: 8,
        fontSize: 14,
        color: "#90caf9",
        fontWeight: 700,
        lineHeight: 1,
      }}
    >
      +
    </Box>
    <Box
      sx={{
        width: 72,
        height: 72,
        borderRadius: "50%",
        bgcolor: "#e3f2fd",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <CheckIcon sx={{ fontSize: 38, color: "#1565c0", fontWeight: 700 }} />
    </Box>
  </Box>
);

const completionButtonSx = {
  textTransform: "none",
  fontWeight: 600,
  fontSize: { xs: "0.875rem", sm: "0.9rem" },
  borderRadius: "8px",
  minHeight: 48,
  boxShadow: "none",
  whiteSpace: { xs: "normal", md: "nowrap" },
  flex: { xs: "none", md: 1 },
  width: { xs: "100%", md: "auto" },
  minWidth: 0,
  px: { xs: 1.5, sm: 2 },
  py: 1.25,
  lineHeight: 1.25,
  boxSizing: "border-box",
  "& .MuiButton-startIcon": {
    marginRight: "6px",
    marginLeft: 0,
  },
};

const LockDivider = () => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      my: 2.5,
      width: "100%",
    }}
  >
    <Box sx={{ flex: 1, height: "1px", bgcolor: "#bbdefb" }} />
    <Box
      sx={{
        mx: 1.5,
        width: 36,
        height: 36,
        borderRadius: "50%",
        bgcolor: "#e3f2fd",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <LockIcon sx={{ fontSize: 18, color: "#1565c0" }} />
    </Box>
    <Box sx={{ flex: 1, height: "1px", bgcolor: "#bbdefb" }} />
  </Box>
);

const ConfirmationDialog = ({
  open,
  onClose,
  loading,
  onConfirm,
  title,
  description,
  alertStatus,
  detailedDescription,
  longText,
  confirmLabel = "Complete Document",
  cancelLabel = "Continue Editing",
}) => {
  const [expanded] = useState(false);

  const isCompletionDialog = alertStatus !== "delete";
  const dialogTitle =
    title && title !== "Confirm Action" ? title : "Confirm Completion";
  const dialogDescription =
    description &&
    !description.includes("Are you sure you want to complete")
      ? description
      : "This document will be marked as completed and locked from further edits.";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="confirmation-dialog-title"
      maxWidth={isCompletionDialog ? "sm" : "xs"}
      fullWidth
      PaperProps={{
        sx: {
          zIndex: 20000,
          borderRadius: 2,
          width: "100%",
          maxWidth: isCompletionDialog
            ? { xs: "calc(100vw - 24px)", sm: 520 }
            : undefined,
          minWidth: isCompletionDialog
            ? { xs: "calc(100vw - 24px)", sm: 480 }
            : undefined,
          overflow: "hidden",
          mx: { xs: 1.5, sm: 2 },
        },
      }}
      BackdropProps={{
        sx: {
          zIndex: 19999,
        },
      }}
      sx={{
        zIndex: 20000,
      }}
    >
      <DialogContent
        sx={{
          textAlign: "center",
          pt: 3,
          pb: 0,
          px: { xs: 2.5, sm: 3 },
          overflow: "hidden",
          boxSizing: "border-box",
        }}
      >
        {isCompletionDialog ? (
          <>
            <CompletionIcon />
            <Typography
              id="confirmation-dialog-title"
              variant="h6"
              sx={{
                color: "#1565c0",
                fontWeight: 700,
                fontSize: "1.15rem",
                mb: 1,
              }}
            >
              {dialogTitle}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                lineHeight: 1.6,
                px: 1,
              }}
            >
              {dialogDescription}
            </Typography>
          </>
        ) : (
          <>
            <ErrorIcon
              sx={{
                fontSize: 70,
                color: "error.main",
                marginBottom: "12px",
                marginTop: "8px",
              }}
            />
            <Typography variant="body1">{description}</Typography>
          </>
        )}

        {longText && (
          <Collapse in={expanded} timeout="auto">
            <Typography
              variant="body2"
              sx={{
                mt: 2,
                textAlign: "left",
                whiteSpace: "pre-wrap",
              }}
            >
              {longText}
            </Typography>
          </Collapse>
        )}

        {detailedDescription && (
          <Typography
            variant="body2"
            color="error"
            sx={{ marginTop: "12px", px: 1 }}
          >
            {detailedDescription}
          </Typography>
        )}

        {isCompletionDialog && <LockDivider />}
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: "center",
          alignItems: "stretch",
          flexDirection: { xs: "column", md: "row" },
          flexWrap: "nowrap",
          gap: 1.5,
          px: { xs: 2.5, sm: 3 },
          pb: 3,
          pt: isCompletionDialog ? 0 : 2,
          width: "100%",
          boxSizing: "border-box",
          overflow: "hidden",
        }}
      >
        {isCompletionDialog ? (
          <>
            <Button
              fullWidth
              variant="contained"
              onClick={onConfirm}
              disabled={loading}
              startIcon={
                loading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <CheckIcon />
                )
              }
              sx={{
                ...completionButtonSx,
                bgcolor: "#28c76f",
                "&:hover": {
                  bgcolor: "#1f9d57",
                  boxShadow: "none",
                },
              }}
            >
              {confirmLabel}
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={onClose}
              disabled={loading}
              startIcon={<EditIcon />}
              sx={{
                ...completionButtonSx,
                bgcolor: "#ea5455",
                "&:hover": {
                  bgcolor: "#d63031",
                  boxShadow: "none",
                },
              }}
            >
              {cancelLabel}
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="contained"
              color="success"
              onClick={onConfirm}
              disabled={loading}
              startIcon={
                loading && <CircularProgress size={20} color="inherit" />
              }
              sx={{
                marginRight: "8px",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "darkgreen",
                  boxShadow: "none",
                },
              }}
            >
              Yes
            </Button>
            <Button
              sx={{
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "rgb(255, 0, 0)",
                  boxShadow: "none",
                },
              }}
              variant="contained"
              color="error"
              onClick={onClose}
            >
              No
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmationDialog;
